import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { Usuario } from '../types';
import usuarioService from '../services/usuarioService';
import { useAuth } from './AuthContext';

interface UsuarioContextType {
  usuarios: Usuario[];
  carregando: boolean;
  erro: string;
  carregarUsuarios: () => Promise<void>;
  atualizarListaUsuarios: () => Promise<void>;
}

const UsuarioContext = createContext<UsuarioContextType | undefined>(undefined);

export const UsuarioProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [usuarios, setUsuarios] = useState<Usuario[]>([]);
  const [carregando, setCarregando] = useState<boolean>(true);
  const [erro, setErro] = useState<string>('');
  const { usuario } = useAuth();
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  // Função para carregar usuários
  const carregarUsuarios = useCallback(async () => {
    setCarregando(true);
    try {
      const resposta = await usuarioService.obterTodos();
      if (resposta.success && resposta.usuarios) {
        setUsuarios(resposta.usuarios);
        setErro('');
      } else {
        throw new Error(resposta.message || 'Erro ao carregar usuários');
      }
    } catch (error: any) {
      console.error('Erro ao carregar usuários:', error);
      setErro('Não foi possível carregar os usuários. Tente novamente mais tarde.');
    } finally {
      setCarregando(false);
    }
  }, []);

  // Função para atualizar a lista de usuários (pode ser chamada manualmente)
  const atualizarListaUsuarios = async () => {
    if (!usuario) return;
    try {
      const resposta = await usuarioService.obterTodos();
      if (resposta.success && resposta.usuarios) {
        setUsuarios(resposta.usuarios);
      }
    } catch (error) {
      console.error('Erro ao atualizar lista de usuários:', error);
    }
  };

  // Configurar intervalo de atualização da lista de usuários
  useEffect(() => {
    if (usuario) {
      // Carregar usuários inicialmente
      carregarUsuarios();

      intervalRef.current = setInterval(() => {
        atualizarListaUsuarios();
      }, 1800000); // 30 minutos
    }
    
    // Limpar intervalo quando o componente for desmontado
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [usuario]);

  return (
    <UsuarioContext.Provider
      value={{
        usuarios,
        carregando,
        erro,
        carregarUsuarios,
        atualizarListaUsuarios
      }}
    >
      {children}
    </UsuarioContext.Provider>
  );
};

export const useUsuario = (): UsuarioContextType => {
  const context = useContext(UsuarioContext);
  if (context === undefined) {
    throw new Error('useUsuario deve ser usado dentro de um UsuarioProvider');
  }
  return context;
};
